import React, { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Grid,
  Checkbox,
  FormControlLabel,
  Collapse,
  IconButton,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material'
import SignatureCanvas from 'react-signature-canvas'
import { format } from 'date-fns'
import { User } from '@/types'
import { ConsentService } from '@/services/consentService'
import { fetchUserIpAddress } from '@/utils/ipAddress'
import LOGO from '@/assets/pillarThera.png'

interface PatientIntakeConsentDialogProps {
  open: boolean
  user: User
  onConsentSubmitted: () => void
}

interface ConsentSection {
  id: string
  title: string
  content: string[]
  acknowledgement: string
  isOptional?: boolean
  optionalLabel?: string
}

const CONSENT_SECTIONS: ConsentSection[] = [
  {
    id: 'consent-to-treat',
    title: 'Consent to Treat & Informed Consent',
    content: [
      'I authorize the licensed Providers connected through PillarThera to evaluate, treat, and provide therapy services as agreed in my service plan. I understand:',
      '• Therapy may include physical, occupational, or speech-language treatment.',
      '• I may stop treatment at any time by notifying my Provider.',
      '• The Provider will explain goals, expected benefits, and potential risks.',
      '• No specific outcome or improvement is guaranteed.',
      'I consent to therapy services delivered through Providers using the PillarThera Platform.',
    ],
    acknowledgement:
      'I consent to therapy services delivered through Providers using the PillarThera Platform.',
  },
  {
    id: 'hipaa-notice',
    title: 'HIPAA Notice of Privacy Practices — Acknowledgment',
    content: [
      "I acknowledge that I have received (or had access to) PillarThera's HIPAA Notice of Privacy Practices, which explains how my health information may be used and disclosed. I understand:",
      '• My PHI is protected under federal and state law.',
      '• I may request restrictions or ask questions at any time.',
      '• I may request a digital or paper copy of the Notice.',
      'I acknowledge receipt and understanding of the HIPAA Notice.',
    ],
    acknowledgement:
      'I acknowledge receipt and understanding of the HIPAA Notice.',
  },
  {
    id: 'financial-responsibility',
    title: 'Financial Responsibility & Payment Policy',
    content: [
      'I understand:',
      '• The first therapy visit booked through PillarThera is an evaluation session, which I must pay out-of-pocket, as insurance does not cover it through this workflow.',
      '• I am responsible for co-pays, deductibles, non-covered services, or any balances not paid by insurance.',
      '• Payment for out-of-pocket services will be processed through the PillarThera Patient Portal.',
      '• I will receive invoices for private-pay services and agree to pay them promptly.',
      '• Estimates are not guarantees of insurance payment.',
      'I accept financial responsibility for applicable charges.',
    ],
    acknowledgement:
      'I accept financial responsibility for applicable charges.',
  },
  {
    id: 'assignment-of-benefits',
    title: 'Assignment of Benefits (AOB)',
    content: [
      'I authorize my insurance company to pay benefits directly to the Provider for services rendered. I understand:',
      '• This authorization applies to all therapy services under my contract.',
      '• I am financially responsible for any amounts not covered by insurance.',
      '• This authorization remains valid until I revoke it in writing.',
      'I assign insurance benefits directly to my Provider.',
    ],
    acknowledgement: 'I assign insurance benefits directly to my Provider.',
  },
  {
    id: 'consent-for-communication',
    title: 'Consent for Communication (SMS, Email, Portal Messages)',
    content: [
      'I authorize PillarThera and my Providers to contact me using:',
      '• Email',
      '• SMS/text messages',
      '• In-app secure messaging',
      'I understand:',
      '• These communications may include scheduling, reminders, billing updates, or therapy-related notifications.',
      '• Text messaging is not fully secure; PHI will not be sent by SMS.',
      '• I may opt out of text or email at any time.',
      'I consent to receive communications related to my care.',
    ],
    acknowledgement: 'I consent to receive communications related to my care.',
  },
  {
    id: 'evv-consent',
    title: 'EVV (Electronic Visit Verification) Consent',
    content: [
      'For in-home therapy, I authorize Providers to record:',
      '• Check-in and check-out times',
      '• Approximate visit location (GPS-based)',
      '• Required service documentation for compliance',
      'I understand this information is used only for regulatory, billing, and documentation purposes and is stored securely.',
      'I consent to EVV data collection for home-based visits.',
    ],
    acknowledgement: 'I consent to EVV data collection for home-based visits.',
  },
  {
    id: 'minor-guardian-consent',
    title: 'Minor / Guardian Consent',
    content: [
      'I certify that I am the parent or legal guardian of the minor receiving therapy. I authorize:',
      '• Evaluation and treatment',
      '• Documentation and coordination of care',
      '• Communication with relevant caregivers',
      'I understand all policies in this intake packet apply to the minor.',
      'I consent on behalf of the minor patient.',
    ],
    acknowledgement: 'I consent on behalf of the minor patient.',
    isOptional: true,
    optionalLabel: 'Skip if patient is an adult',
  },
  {
    id: 'pillarvoice-consent',
    title: 'PillarVoice™ Recording & AI Scribe Consent',
    content: [
      'I understand that:',
      '• Providers may use PillarVoice™ to record therapy-related notes or session summaries.',
      "• Audio is processed securely and never stored on my or the Provider's device.",
      '• Only the text report, not the audio, is saved in my clinical record.',
      '• PillarVoice™ does not replace clinical judgment or manual note review.',
      'I consent to the use of PillarVoice™ for clinical documentation.',
    ],
    acknowledgement:
      'I consent to the use of PillarVoice™ for clinical documentation.',
  },
  {
    id: 'medical-history',
    title: 'Medical History & Safety Questionnaire',
    content: [
      'I confirm that I have provided accurate information regarding:',
      '• Medical conditions',
      '• Surgical history',
      '• Medications',
      '• Allergies',
      '• Mobility or safety concerns',
      '• Therapy goals',
      'I understand this information is used to guide safe and appropriate therapy.',
      'I confirm the accuracy of my medical and safety information.',
    ],
    acknowledgement:
      'I confirm the accuracy of my medical and safety information.',
  },
]

const PatientIntakeConsentDialog: React.FC<PatientIntakeConsentDialogProps> = ({
  open,
  user,
  onConsentSubmitted,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const patientSignaturePadRef = useRef<SignatureCanvas>(null)
  const guardianSignaturePadRef = useRef<SignatureCanvas>(null)

  // Form state
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set([CONSENT_SECTIONS[0].id])
  )
  const [patientSignature, setPatientSignature] = useState<string | null>(null)
  const [guardianSignature, setGuardianSignature] = useState<string | null>(
    null
  )
  const [guardianName, setGuardianName] = useState('')
  // Three certification checkboxes - all checked by default
  const [certification1, setCertification1] = useState(true)
  const [certification2, setCertification2] = useState(true)
  const [certification3, setCertification3] = useState(true)

  // API state
  const [ipAddress, setIpAddress] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isFetchingIp, setIsFetchingIp] = useState(true)

  const patientFullName = `${user.firstName} ${user.lastName}`
  const dateOfConsent = format(new Date(), 'MM/dd/yyyy')

  useEffect(() => {
    if (open) {
      fetchUserIpAddress()
        .then(ip => {
          setIpAddress(ip)
          setIsFetchingIp(false)
        })
        .catch(() => {
          setIpAddress('Unknown')
          setIsFetchingIp(false)
        })
    }
  }, [open])

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev)
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId)
      } else {
        newSet.add(sectionId)
      }
      return newSet
    })
  }

  const handleClearPatientSignature = () => {
    if (patientSignaturePadRef.current) {
      patientSignaturePadRef.current.clear()
      setPatientSignature(null)
    }
  }

  const handleClearGuardianSignature = () => {
    if (guardianSignaturePadRef.current) {
      guardianSignaturePadRef.current.clear()
      setGuardianSignature(null)
    }
  }

  const handlePatientSignatureEnd = () => {
    if (
      patientSignaturePadRef.current &&
      !patientSignaturePadRef.current.isEmpty()
    ) {
      const dataURL = patientSignaturePadRef.current.toDataURL('image/png')
      setPatientSignature(dataURL)
    } else {
      setPatientSignature(null)
    }
  }

  const handleGuardianSignatureEnd = () => {
    if (
      guardianSignaturePadRef.current &&
      !guardianSignaturePadRef.current.isEmpty()
    ) {
      const dataURL = guardianSignaturePadRef.current.toDataURL('image/png')
      setGuardianSignature(dataURL)
    } else {
      setGuardianSignature(null)
    }
  }

  // Check if form is complete
  const isFormComplete = () => {
    return (
      !!patientSignature &&
      !!guardianSignature &&
      guardianName.trim().length > 0 &&
      certification1 &&
      certification2 &&
      certification3
    )
  }

  const handleSubmit = async () => {
    if (!patientSignature) {
      setError('Please provide your signature')
      return
    }

    if (!guardianSignature || !guardianName.trim()) {
      setError('Please provide guardian signature and name')
      return
    }

    if (!certification1 || !certification2 || !certification3) {
      setError('Please confirm all certification statements')
      return
    }

    setLoading(true)
    setError(null)

    try {
      await ConsentService.submitPatientConsent({
        ipAddress,
        patientSignature,
        guardianSignature: guardianSignature || undefined,
        guardianName: guardianName.trim() || undefined,
      })

      onConsentSubmitted()
    } catch (err: any) {
      console.error('Error submitting consent:', err)
      setError(
        err.response?.data?.message ||
          'Failed to submit consent. Please try again.'
      )
    } finally {
      setLoading(false)
    }
  }

  const canSubmit = isFormComplete() && !loading

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return
        }
      }}
      disableEscapeKeyDown
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          maxHeight: isMobile ? '100vh' : '95vh',
          borderRadius: isMobile ? 0 : 3,
          bgcolor: '#FAFBFC',
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Fixed Header with Logo */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            bgcolor: 'white',
            borderBottom: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            py: { xs: 3, md: 2 },
            // pb: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '100%',
              px: { xs: 2, md: 4 },
              py: { xs: 2, md: 4 },
              position: 'relative',
            }}
          >
            {/* Left spacer - flex: 1 to balance */}
            <Box sx={{ flex: 1 }} />

            {/* Logo - Centered */}
            <Box
              component="img"
              src={LOGO}
              alt="PillarThera Logo"
              sx={{
                height: { xs: 60, md: 80 },
                width: 'auto',
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            />

            {/* Progress Chip - Right End */}
            {/* <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Chip
                icon={<DescriptionIcon sx={{ fontSize: { xs: 16, md: 18 } }} />}
                label="Consent Packet"
                color="default"
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  fontWeight: 600,
                }}
              />
            </Box> */}
          </Box>
        </Box>

        {/* Scrollable Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: { xs: 2, md: 4 },
            py: { xs: 3, md: 2 },
          }}
        >
          {/* Title in Content */}
          <Box sx={{ mb: { xs: 3, md: 4 }, textAlign: 'center' }}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                color: 'primary.main',
                fontSize: { xs: '1.5rem', md: '2rem' },
                mb: 1,
              }}
            >
              Patient Intake & Consent Packet
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                fontSize: { xs: '0.875rem', md: '1rem' },
              }}
            >
              Please review and consent to each section below
            </Typography>
          </Box>

          {/* Consent Sections */}
          <Box sx={{ mb: 4 }}>
            {CONSENT_SECTIONS.map((section, index) => {
              const isExpanded = expandedSections.has(section.id)

              return (
                <Paper
                  key={section.id}
                  elevation={0}
                  sx={{
                    mb: 2.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    transition: 'all 0.2s ease',
                    bgcolor: 'white',
                    '&:hover': {
                      borderColor: 'primary.light',
                    },
                  }}
                >
                  {/* Section Header */}
                  <Box
                    onClick={() => toggleSection(section.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: { xs: 2, md: 3, xl: 4 },
                      py: { xs: 2, md: 2.5, xl: 3 },
                      cursor: 'pointer',
                      bgcolor: isExpanded ? 'grey.50' : 'transparent',
                      transition: 'background-color 0.2s',
                      '&:hover': {
                        bgcolor: 'grey.100',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 24, md: 28 },
                          height: { xs: 24, md: 28 },
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          sx={{
                            fontSize: { xs: '0.75rem', md: '0.875rem' },
                          }}
                        >
                          {index + 1}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          sx={{
                            color: 'text.primary',
                            fontSize: { xs: '1rem', md: '1.125rem' },
                          }}
                        >
                          {section.title}
                        </Typography>
                        {section.isOptional && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: '0.7rem', md: '0.75rem' },
                            }}
                          >
                            {section.optionalLabel}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <IconButton size="small">
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>

                  {/* Section Content */}
                  <Collapse in={isExpanded}>
                    <Box
                      sx={{
                        px: { xs: 2, md: 3 },
                        pb: { xs: 3, md: 3.5 },
                      }}
                    >
                      <Divider sx={{ mb: 3 }} />

                      {/* Section Content */}
                      <Box sx={{ mb: 3 }}>
                        {section.content.map((paragraph, pIndex) => (
                          <Typography
                            key={pIndex}
                            variant="body2"
                            sx={{
                              mb: 1.5,
                              color: 'text.secondary',
                              lineHeight: 1.8,
                              pl: paragraph.startsWith('•') ? 2 : 0,
                              fontSize: { xs: '0.875rem', md: '1rem' },
                            }}
                          >
                            {paragraph}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </Collapse>
                </Paper>
              )
            })}
          </Box>

          <Divider sx={{ my: { xs: 4, md: 5 } }}>
            <Chip label="Final Signature" sx={{ fontWeight: 600 }} />
          </Divider>

          {/* Final Certification */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              mb: 3,
              border: '2px solid',
              borderColor: 'primary.light',
              borderRadius: 2,
              bgcolor: 'white',
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                mb: 3,
                color: 'primary.main',
                fontSize: { xs: '1.125rem', md: '1.25rem' },
              }}
            >
              Final Certification
            </Typography>

            <Typography
              variant="body1"
              sx={{
                mb: 2,
                lineHeight: 1.8,
                fontSize: { xs: '0.875rem', md: '1rem' },
                color: 'text.secondary',
              }}
            >
              I certify that:
            </Typography>

            {/* Three Certification Checkboxes */}
            <Box
              sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={certification1}
                    onChange={e => {
                      setCertification1(e.target.checked)
                      setError(null)
                    }}
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                      '&.Mui-checked': {
                        color: 'success.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      lineHeight: 1.8,
                    }}
                  >
                    I have read and understand all sections of this Intake &
                    Consent Packet.
                  </Typography>
                }
                sx={{ mb: 1.5 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={certification2}
                    onChange={e => {
                      setCertification2(e.target.checked)
                      setError(null)
                    }}
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                      '&.Mui-checked': {
                        color: 'success.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      lineHeight: 1.8,
                    }}
                  >
                    I have had the opportunity to ask questions.
                  </Typography>
                }
                sx={{ mb: 1.5 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={certification3}
                    onChange={e => {
                      setCertification3(e.target.checked)
                      setError(null)
                    }}
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                      '&.Mui-checked': {
                        color: 'success.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      lineHeight: 1.8,
                    }}
                  >
                    I agree to all terms and authorize therapy and related
                    processes.
                  </Typography>
                }
              />
            </Box>

            {/* Patient Signature */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{
                  mb: 2,
                  color: 'text.primary',
                  fontSize: { xs: '1rem', md: '1.125rem' },
                }}
              >
                Patient Signature
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    Patient Name
                  </Typography>
                  <TextField
                    value={patientFullName}
                    disabled
                    fullWidth
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: 'text.primary',
                        fontWeight: 500,
                        fontSize: { xs: '0.875rem', md: '1rem' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    Date
                  </Typography>
                  <TextField
                    value={dateOfConsent}
                    disabled
                    fullWidth
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: 'text.primary',
                        fontWeight: 500,
                        fontSize: { xs: '0.875rem', md: '1rem' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    Signature (draw below)
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{
                      border: '2px solid',
                      borderColor: patientSignature
                        ? 'success.light'
                        : 'divider',
                      borderRadius: 2,
                      p: 1,
                      bgcolor: 'white',
                      display: 'flex',
                      justifyContent: 'center',
                      overflow: 'auto',
                      '& canvas': {
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        cursor: 'crosshair',
                        maxWidth: '100%',
                        height: 'auto',
                      },
                    }}
                  >
                    <SignatureCanvas
                      ref={patientSignaturePadRef}
                      canvasProps={{
                        width: isMobile ? 300 : 500,
                        height: 150,
                        className: 'signature-canvas',
                        style: { maxWidth: '100%', height: 'auto' },
                      }}
                      onEnd={handlePatientSignatureEnd}
                    />
                  </Paper>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleClearPatientSignature}
                    sx={{ mt: 1, textTransform: 'none' }}
                  >
                    Clear Signature
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {/* Guardian Signature - Always Required */}
            <Box sx={{ mb: 4 }}>
              <Divider sx={{ mb: 3 }} />
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{
                  mb: 2,
                  color: 'text.primary',
                  fontSize: { xs: '1rem', md: '1.125rem' },
                }}
              >
                Guardian Signature
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    Guardian Name *
                  </Typography>
                  <TextField
                    value={guardianName}
                    onChange={e => setGuardianName(e.target.value)}
                    placeholder="Enter guardian's full name"
                    fullWidth
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: { xs: '0.875rem', md: '1rem' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    Date
                  </Typography>
                  <TextField
                    value={dateOfConsent}
                    disabled
                    fullWidth
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: 'text.primary',
                        fontWeight: 500,
                        fontSize: { xs: '0.875rem', md: '1rem' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    Guardian Signature (draw below) *
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{
                      border: '2px solid',
                      borderColor: guardianSignature
                        ? 'success.light'
                        : 'divider',
                      borderRadius: 2,
                      p: 1,
                      bgcolor: 'white',
                      display: 'flex',
                      justifyContent: 'center',
                      overflow: 'auto',
                      '& canvas': {
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        cursor: 'crosshair',
                        maxWidth: '100%',
                        height: 'auto',
                      },
                    }}
                  >
                    <SignatureCanvas
                      ref={guardianSignaturePadRef}
                      canvasProps={{
                        width: isMobile ? 300 : 500,
                        height: 150,
                        className: 'signature-canvas',
                        style: { maxWidth: '100%', height: 'auto' },
                      }}
                      onEnd={handleGuardianSignatureEnd}
                    />
                  </Paper>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleClearGuardianSignature}
                    sx={{ mt: 1, textTransform: 'none' }}
                  >
                    Clear Signature
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {/* Consent Details */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, md: 2.5 },
                bgcolor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      fontSize: { xs: '0.65rem', md: '0.7rem' },
                    }}
                  >
                    IP Address
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      fontSize: { xs: '0.875rem', md: '1rem' },
                    }}
                  >
                    {isFetchingIp ? (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <CircularProgress size={12} />
                        <span>Loading...</span>
                      </Box>
                    ) : (
                      ipAddress
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      fontSize: { xs: '0.65rem', md: '0.7rem' },
                    }}
                  >
                    Date & Time
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      fontSize: { xs: '0.875rem', md: '1rem' },
                    }}
                  >
                    {format(new Date(), 'MM/dd/yyyy h:mm a')}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Paper>

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: { xs: 2, md: 3 },
          bgcolor: 'white',
          borderTop: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 -4px 12px rgba(0,0,0,0.05)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: { xs: 'none', md: 'block' },
              fontSize: { xs: '0.875rem', md: '1rem' },
            }}
          >
            By submitting, you agree to the terms outlined in this consent
            packet.
          </Typography>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
            size="large"
            sx={{
              minWidth: { xs: '100%', md: 200 },
              py: 1.5,
              fontSize: { xs: '0.875rem', md: '1rem' },
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              boxShadow: canSubmit
                ? '0 4px 12px rgba(25, 118, 210, 0.3)'
                : 'none',
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Submit Consent Packet'
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default PatientIntakeConsentDialog
